import { api } from "./client";

// External Intake & Collection v1.0 PR 8 (Series 1/6) — staff client for
// the "create & send a package" surface. Mirrors the backend routes on
//   PhoenixCapital.Api/Controllers/Collection/CollectionPackagesController.cs
//   PhoenixCapital.Api/Controllers/Collection/ExternalRecipientsController.cs
//   PhoenixCapital.Api/Controllers/Portal/PortalAdminController.cs
// and the DTOs in PhoenixCapital.Core/DTOs/Collection/CollectionDtos.cs.

import type { CollectionItemKind, MaterialAudience } from "./collectionTemplates";
import type { ApprovalChainStatus } from "./approvalChains";

export type CollectionItemStatus =
  | "Pending"
  | "InProgress"
  | "SubmittedForReview"
  | "ChangesRequested"
  | "Approved"
  | "Waived";

export type CollectionPackageStatus =
  | "Draft"
  | "Sent"
  | "InProgress"
  | "Submitted"
  | "PartiallyApproved"
  | "Completed"
  | "Cancelled"
  | "Archived";

export interface ExternalRecipientDto {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  linkedCounterpartyType: string | null;
  linkedCounterpartyId: string | null;
  portalUserId: string | null;
  createdAt: string;
  // FL-10 — the linked PortalUser's structured account type, when one
  // exists. Lets the composer lock the party-role field for known emails.
  linkedAccountType: MaterialAudience | null;
}

export interface UpsertExternalRecipientPayload {
  fullName: string;
  email: string;
  phone: string | null;
  linkedCounterpartyType: string | null;
  linkedCounterpartyId: string | null;
}

export interface CollectionItemDto {
  id: string;
  packageId: string;
  templateId: string;
  templateVersion: number;
  templateName: string;
  kind: CollectionItemKind;
  status: CollectionItemStatus;
  reviewerNote: string | null;
  reviewerId: string | null;
  decisionAt: string | null;
  primaryDocumentId: string | null;
  assessmentDocumentId: string | null;
  createdAt: string;
  modifiedAt: string;
  /** Special forms — per-send letter-body override on a draft item; null =
   *  the item renders the template's canonical body. */
  bodyMarkdownOverride?: string | null;
  /** Special forms — stamped when the item was sent with a frozen body
   *  snapshot (see getItemSentSnapshot). */
  sentSnapshotAt?: string | null;
  /** WS1 — the item is archived (hidden from active reads; surfaced only when
   *  the detail read is called with includeArchived so Unarchive works). */
  isArchived?: boolean;
  /** WS1 — the item is UNTOUCHED (recipient hasn't acted), so soft-Delete is
   *  offered; a touched item is Archive-only. Server-computed on the staff
   *  detail read (false elsewhere). */
  canHardDelete?: boolean;
  /** Approval chains (D12) — the item's chain status line ("Awaiting
   *  endorsement — Jane Broker"); null when the item has no chain. */
  approvalChainStatusLabel?: string | null;
  approvalChainStatus?: ApprovalChainStatus | null;
  /** Upload templates (U6b) — the document to download of the template
   *  version this item was issued from (null = none), and when the recipient
   *  first downloaded it (null = not yet). */
  formAttachmentFileName?: string | null;
  formDownloadedAt?: string | null;
}

/** Special forms — the body text frozen at send time for an item. Mirrors
 *  GET /collection/packages/{id}/items/{itemId}/sent-snapshot. */
export interface CollectionItemSentSnapshot {
  itemId: string;
  sentBodySnapshot: string | null;
  sentSnapshotAt: string | null;
}

/**
 * Tier 2 #9 — `recipientPartyRole` moved off the package onto the
 * account (`PortalUser.AccountType`). Reads come back through the
 * recipient's linked portal user; staff sets it at invitation-issue
 * time via `portalInvitationsApi.issue`.
 */
export interface CollectionPackageDto {
  id: string;
  ownerEntityType: string;
  ownerEntityId: string;
  recipientId: string;
  recipientRoleLabel: string | null;
  status: CollectionPackageStatus;
  dueDate: string | null;
  createdAt: string;
  modifiedAt: string;
  sentAt: string | null;
  createdByUserId: string | null;
  /** External Portal v2 PR 3 §10.1 — explicit staff assignee; null falls
   *  back to Deal-assignment derivation in the staging queue. */
  assignedToUserId?: string | null;
  items: CollectionItemDto[];
}

export interface CreateCollectionPackagePayload {
  ownerEntityType: string;
  ownerEntityId: string;
  recipientId: string;
  recipientRoleLabel: string | null;
  dueDate: string | null;
}

/** Mirrors PhoenixCapital.Core.DTOs.Portal.InviteOrNotifyResult — the
 *  unified staff send: "set-password" = token minted + invite email
 *  (account not yet activated); "notified" = login notification only
 *  (account already has a password; no token, expiresAtUtc null). */
export interface InviteOrNotifyResult {
  portalUserId: string;
  mode: "set-password" | "notified";
  expiresAtUtc: string | null;
}

export const externalRecipientsApi = {
  get: (id: string) => api.get<ExternalRecipientDto>(`/collection/recipients/${id}`),
  findByEmail: (email: string) =>
    api.get<ExternalRecipientDto>("/collection/recipients/by-email", { email }),
  create: (body: UpsertExternalRecipientPayload) =>
    api.post<ExternalRecipientDto>("/collection/recipients", body),
  update: (id: string, body: UpsertExternalRecipientPayload) =>
    api.put<ExternalRecipientDto>(`/collection/recipients/${id}`, body),
};

/** F8 — one requested-document row in the party-surface "request view"
 *  slide-out. Mirrors
 *  PhoenixCapital.Core.DTOs.Collection.CollectionPackageItemSummaryDto — the
 *  thin per-item projection returned by the account-scoped package-items read. */
export interface CollectionPackageItemSummary {
  id: string;
  templateName: string;
  kind: CollectionItemKind;
  status: CollectionItemStatus;
  /** WS-F — the item is archived (hidden from the active list; surfaced only
   *  when the account-scoped read is called with includeArchived=true so the
   *  drawer's Unarchive action works). */
  isArchived: boolean;
  /** WS-F — the item is UNTOUCHED (recipient hasn't acted), so soft-Delete is
   *  offered; a touched item is Archive-only. Server-computed. */
  canHardDelete: boolean;
}

/** FL-9 — one row in the global Doc Requests landing page. Mirrors
 *  PhoenixCapital.Core.DTOs.Collection.CollectionPackageQueueItemDto. */
export interface CollectionPackageQueueItem {
  id: string;
  ownerEntityType: string;
  ownerEntityId: string;
  recipientId: string;
  recipientFullName: string;
  recipientEmail: string;
  recipientRoleLabel: string | null;
  status: CollectionPackageStatus;
  dueDate: string | null;
  createdAt: string;
  sentAt: string | null;
  itemsTotal: number;
  itemsCompleted: number;
  // Review loop — non-archived items whose next move is the recipient's
  // (Pending, InProgress, ChangesRequested). Drives "N items awaiting
  // recipient"; items waiting on staff are not counted. Optional for older
  // fixtures; the server always sends it.
  itemsAwaitingRecipient?: number;
}

export interface CollectionPackageQueueQuery {
  ownerEntityType?: string;
  ownerEntityId?: string;
  recipientId?: string;
  status?: CollectionPackageStatus;
  overdueOnly?: boolean;
  /** Case-insensitive contains on the recipient's name or email. */
  search?: string;
}

export const collectionPackagesApi = {
  /** WS1 — includeArchived un-hides archived items so the staff "Show archived"
   *  toggle + Unarchive work; soft-deleted items never return. */
  get: (id: string, opts?: { includeArchived?: boolean }) =>
    api.get<CollectionPackageDto>(
      `/collection/packages/${id}`,
      opts?.includeArchived ? { includeArchived: true } : undefined,
    ),
  listByOwner: (ownerEntityType: string, ownerEntityId: string) =>
    api.get<CollectionPackageDto[]>("/collection/packages", {
      ownerEntityType,
      ownerEntityId,
    }),
  /** FL-9 — global outstanding view; backs the default Doc Requests landing. */
  listOutstanding: (query: CollectionPackageQueueQuery = {}) =>
    api.get<CollectionPackageQueueItem[]>("/collection/packages/outstanding", query),
  create: (body: CreateCollectionPackagePayload) =>
    api.post<CollectionPackageDto>("/collection/packages", body),
  addItem: (
    packageId: string,
    templateId: string,
    savedFormDataJson?: string | null,
    bodyMarkdownOverride?: string | null,
  ) =>
    api.post<CollectionItemDto>(`/collection/packages/${packageId}/items`, {
      templateId,
      savedFormDataJson: savedFormDataJson ?? null,
      bodyMarkdownOverride: bodyMarkdownOverride ?? null,
    }),
  /** Sender-Fields v1 — live letter preview PDF for the composer (template
   *  body token-substituted with the in-progress sender values; no item
   *  created). Returns the PDF blob to show in the iframe. Special forms —
   *  a non-blank bodyMarkdownOverride previews the edited body instead of
   *  the template's canonical one. */
  letterPreview: (
    templateId: string,
    savedFormDataJson: string | null,
    bodyMarkdownOverride?: string | null,
  ) =>
    api.postBlob("/collection/packages/letter-preview", {
      templateId,
      savedFormDataJson,
      bodyMarkdownOverride: bodyMarkdownOverride ?? null,
    }),
  /** Special forms — set/clear the per-send body override on a DRAFT
   *  package item (null/blank = reset to the template body). */
  setItemBodyOverride: (
    packageId: string,
    itemId: string,
    bodyMarkdownOverride: string | null,
  ) =>
    api.put<CollectionItemDto>(
      `/collection/packages/${packageId}/items/${itemId}/body-override`,
      { bodyMarkdownOverride },
    ),
  /** Special forms — the body text frozen when the item was sent. */
  getItemSentSnapshot: (packageId: string, itemId: string) =>
    api.get<CollectionItemSentSnapshot>(
      `/collection/packages/${packageId}/items/${itemId}/sent-snapshot`,
    ),
  removeItem: (packageId: string, itemId: string) =>
    api.delete<null>(`/collection/packages/${packageId}/items/${itemId}`),
  /** WS1 — archive a single item (recoverable). Allowed in any state. */
  archiveItem: (packageId: string, itemId: string) =>
    api.post<CollectionItemDto>(
      `/collection/packages/${packageId}/items/${itemId}/archive`,
    ),
  /** WS1 — restore an archived item. */
  unarchiveItem: (packageId: string, itemId: string) =>
    api.post<CollectionItemDto>(
      `/collection/packages/${packageId}/items/${itemId}/unarchive`,
    ),
  /** WS1 — soft-delete a single item (row retained). 400 when the item is
   *  touched (recipient has acted) — archive it instead. */
  deleteItem: (packageId: string, itemId: string) =>
    api.post<null>(`/collection/packages/${packageId}/items/${itemId}/delete`),
  /** WS1 — soft-delete the whole package (row retained). 400 when any item is
   *  touched — archive it instead. */
  deletePackage: (id: string) =>
    api.post<CollectionPackageDto>(`/collection/packages/${id}/delete`),
  send: (id: string) => api.post<CollectionPackageDto>(`/collection/packages/${id}/send`),
  cancel: (id: string) => api.post<CollectionPackageDto>(`/collection/packages/${id}/cancel`),
  /** External Portal v2 PR 3 §10.5 — soft-hide the package from active
   *  queues without losing audit history. */
  archive: (id: string) =>
    api.post<CollectionPackageDto>(`/collection/packages/${id}/archive`),
  /** Queue UAT round 3 (PROB-F) — restore an archived package to the active
   *  queue. Status is recomputed server-side from the item roll-up. */
  unarchive: (id: string) =>
    api.post<CollectionPackageDto>(`/collection/packages/${id}/unarchive`),
  /** External Portal v2 PR 3 §10.3 — fire an ad-hoc reminder email
   *  through the same path as the cron. */
  remind: (id: string) =>
    api.post<CollectionPackageDto>(`/collection/packages/${id}/remind`),
  /** External Portal v2 PR 3 §10.8 — staff fix-up when a package is
   *  attached to the wrong owner. */
  retargetOwner: (id: string, ownerEntityType: string, ownerEntityId: string) =>
    api.post<CollectionPackageDto>(`/collection/packages/${id}/retarget-owner`, {
      ownerEntityType,
      ownerEntityId,
    }),
  /** External Portal v2 PR 2 PROB-25b — edit the package due date from the
   *  staging-review surface. Pass a YYYY-MM-DD calendar date or null to
   *  clear. Date-only per the PROB-05 rule. */
  updateDueDate: (id: string, dueDate: string | null) =>
    api.patch<CollectionPackageDto>(`/collection/packages/${id}/due-date`, {
      dueDate,
    }),
  /** External Portal v2 PR 3 (PROB-24b) — many-to-many package assignees. */
  listAssignees: (id: string) =>
    api.get<CollectionPackageAssignee[]>(`/collection/packages/${id}/assignees`),
  addAssignee: (id: string, userId: string) =>
    api.post<CollectionPackageAssignee>(`/collection/packages/${id}/assignees`, {
      userId,
    }),
  removeAssignee: (id: string, userId: string) =>
    api.delete<null>(`/collection/packages/${id}/assignees/${userId}`),
};

/** External Portal v2 PR 3 (PROB-24b) — a resolved package assignee. */
export interface CollectionPackageAssignee {
  userId: string;
  displayName: string;
  email: string;
  roleCode: string | null;
  assignedAtUtc: string;
  assignedByUserId: string | null;
}

/** Staff trigger that emails the recipient about a package. The
 *  send/{id}/send call transitions the package status; this call sends
 *  the invitation. Invite-system consolidation: it hits the unified
 *  endpoint — a not-yet-activated account gets the v2 set-password
 *  invite, an activated account gets a "new request waiting" login
 *  notification (the legacy magic-link issue endpoint serves only
 *  outstanding tokens). Tier 2 #9 — first-time issue requires the
 *  account type; re-issues against an existing account omit it. */
export const portalInvitationsApi = {
  issue: (packageId: string, accountType?: MaterialAudience) => {
    const qs = accountType ? `?accountType=${accountType}` : "";
    return api.post<InviteOrNotifyResult>(
      `/portal/admin/invitations/${packageId}/invite${qs}`,
    );
  },
};
